import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { HandHeart, Plus, Trash2, Shield } from "lucide-react";

const SCOPES = ["Mood trends", "Journal summaries", "Crisis alerts", "Goals & habits"];

export default function SupportCircle() {
  const [members, setMembers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", relationship: "", contact: "", shared_scope: [] });

  const load = () => api.get("/support-circle").then(({ data }) => setMembers(data.members || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.name.trim()) return toast.error("Add a name");
    try { await api.post("/support-circle", form); setOpen(false); setForm({ name: "", relationship: "", contact: "", shared_scope: [] }); load(); toast.success("Added to your circle"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (id) => { await api.delete(`/support-circle/${id}`); load(); };
  const toggleScope = (s) => setForm((f) => ({ ...f, shared_scope: f.shared_scope.includes(s) ? f.shared_scope.filter((x) => x !== s) : [...f.shared_scope, s] }));

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl font-semibold sm:text-4xl">Trusted Support Circle</h1>
          <p className="mt-2 max-w-lg text-muted-foreground">The people you'd lean on. You control exactly what's shared — nothing is sent automatically.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="rounded-full gap-2" data-testid="add-member-button"><Plus className="h-4 w-4" strokeWidth={1.5} /> Add person</Button></DialogTrigger>
          <DialogContent className="rounded-3xl">
            <DialogHeader><DialogTitle className="font-serif text-2xl">Add to your circle</DialogTitle></DialogHeader>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name" data-testid="member-name-input" className="rounded-xl" />
            <Input value={form.relationship} onChange={(e) => setForm({ ...form, relationship: e.target.value })} placeholder="Relationship (e.g. Partner, Therapist)" data-testid="member-relationship-input" className="rounded-xl" />
            <Input value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} placeholder="Email or phone (optional)" data-testid="member-contact-input" className="rounded-xl" />
            <div>
              <p className="mb-2 text-sm font-medium">What may be shared with them?</p>
              <div className="space-y-2">
                {SCOPES.map((s) => (
                  <label key={s} className="flex items-center gap-2 text-sm">
                    <Checkbox checked={form.shared_scope.includes(s)} onCheckedChange={() => toggleScope(s)} data-testid={`scope-${s.replace(/\s+/g, "-").toLowerCase()}`} /> {s}
                  </label>
                ))}
              </div>
            </div>
            <Button onClick={add} className="rounded-full" data-testid="save-member-button">Add person</Button>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-6 flex items-start gap-2 rounded-2xl bg-sage/10 p-4 text-sm text-sage">
        <Shield className="mt-0.5 h-4 w-4 flex-shrink-0" strokeWidth={1.5} />
        <p>Your circle is private. We never share anything without your explicit action, except where required by law.</p>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
        {members.length === 0 && <p className="text-sm text-muted-foreground">Your circle is empty. Add the people you trust most.</p>}
        {members.map((m, i) => (
          <motion.div key={m.member_id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: i * 0.05 }}
                      className="ambient-shadow group rounded-3xl border border-border/60 bg-card p-5">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10"><HandHeart className="h-5 w-5 text-primary" strokeWidth={1.5} /></div>
                <div><p className="font-medium">{m.name}</p><p className="text-sm text-muted-foreground">{m.relationship}</p></div>
              </div>
              <button onClick={() => del(m.member_id)} className="text-muted-foreground opacity-0 group-hover:opacity-100" data-testid={`delete-member-${m.member_id}`}><Trash2 className="h-4 w-4" strokeWidth={1.5} /></button>
            </div>
            {m.shared_scope?.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5">{m.shared_scope.map((s) => <span key={s} className="rounded-full bg-secondary px-2.5 py-0.5 text-xs text-secondary-foreground">{s}</span>)}</div>
            )}
          </motion.div>
        ))}
      </div>
    </div>
  );
}
